import React, { useState, useEffect, forwardRef, useCallback, useRef, useImperativeHandle } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTheme } from '@mui/material';

interface RichTextEditorProps {
  value: string | null | undefined;
  setValue: (v: string) => void;
  placeholder?: string | undefined;
  style?: React.CSSProperties | undefined;
  disabled?: boolean;
  onChangeEvent?: boolean;
  toolbarAtBottom?: boolean;
}

/** Helper function to get indent level from a list item */
const getIndentLevel = (li: HTMLElement): number => {
  for (let i = 1; i <= 9; i++) {
    if (li.classList.contains(`ql-indent-${i}`)) return i;
  }
  return 0;
};

/** Convert number to letter (1=a, 2=b, etc.) */
const toAlpha = (num: number): string => {
  return String.fromCharCode(96 + num); // 97 = 'a'
};

/** Convert number to roman numeral */
const toRoman = (num: number): string => {
  const romanNumerals: [number, string][] = [
    [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']
  ];
  let result = '';
  for (const [value, numeral] of romanNumerals) {
    while (num >= value) {
      result += numeral;
      num -= value;
    }
  }
  return result;
};

/** Manually fix nested list numbering by setting data attributes */
const fixNestedListCounters = (editorElement: HTMLElement | null) => {
  if (!editorElement) return;
  
  const orderedLists = editorElement.querySelectorAll('ol');
  orderedLists.forEach((ol: HTMLOListElement) => {
    const items = ol.querySelectorAll('li');
    
    // Track counters for each indent level
    const counters: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    let prevIndent = -1;
    
    items.forEach((li: HTMLLIElement) => {
      const currentIndent = getIndentLevel(li);
      
      // If we moved to a deeper indent level, reset that level's counter
      if (currentIndent > prevIndent) {
        counters[currentIndent] = 0;
      }
      
      // Increment the counter for the current indent level
      counters[currentIndent]++;
      
      // Format the marker based on indent level
      let marker: string;
      if (currentIndent === 0) {
        marker = `${counters[currentIndent]}.`;
      } else if (currentIndent % 3 === 1) {
        // Indent 1, 4, 7: lowercase letters
        marker = `${toAlpha(counters[currentIndent])}.`;
      } else if (currentIndent % 3 === 2) {
        // Indent 2, 5, 8: roman numerals
        marker = `${toRoman(counters[currentIndent])}.`;
      } else {
        // Indent 3, 6, 9: numbers again
        marker = `${counters[currentIndent]}.`;
      }
      
      // Set the marker as a data attribute
      li.setAttribute('data-list-marker', marker);
      li.classList.add('custom-list-marker');
      
      prevIndent = currentIndent;
    });
  });
};

const RichTextEditor = forwardRef(
  (
    { value, setValue, placeholder, style, disabled, onChangeEvent, toolbarAtBottom }: RichTextEditorProps,
    ref: React.ForwardedRef<ReactQuill | null>
  ) => {
    const internalRef = useRef<ReactQuill | null>(null);
    
    // Expose the internal ref to the parent via forwardRef
    useImperativeHandle(ref, () => internalRef.current as ReactQuill);
    
    /** editorValue is used to keep track of the user's active typing. `props.value` will be updated when the user stops typing. (performance) */
    const [editorValue, setEditorValue] = useState(
      value?.replaceAll(/style="[a-zA-Z0-9:;.\s()\-,]*"/gi, '') ?? ''
    );

    useEffect(() => {
      setEditorValue(
        value?.replaceAll(/style="[a-zA-Z0-9:;.\s()\-,]*"/gi, '') ?? ''
      );
    }, [value]);

    useEffect(() => {
      // Debounce our setValue functrion here so we can wait for the user to stop typing (helps with performance)
      if(!onChangeEvent) {
      const delayDebounceFn = setTimeout(() => {
        // remove styles from pasted html
        setValue(
          editorValue.replaceAll(/style="[a-zA-Z0-9:;.\s()\-,]*"/gi, '')
        );
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    }
    }, [editorValue, setValue]);

    const handleTextChange = (newVal: string) => {
      setEditorValue(newVal);
      setValue(newVal)
    }

    // Fix nested list counter reset after each content change
    useEffect(() => {
      const timer = setTimeout(() => {
        if (internalRef.current) {
          const editor = internalRef.current.getEditor();
          const editorElement = editor?.root as HTMLElement | null;
          fixNestedListCounters(editorElement);
        }
      }, 50);
      return () => clearTimeout(timer);
    }, [editorValue]);
    
    const checkCharacterCount = (e: any) => {
      // Only allow up to 4000 characters
      if (editorValue.length > 4000 && e.key !== 'Backspace') {
        e.preventDefault();
      }
    };
    const theme = useTheme();

    return (
      <>
      <ReactQuill
        ref={internalRef}
        placeholder={!!placeholder ? placeholder : 'Begin typing...'}
        onKeyDown={checkCharacterCount}
        modules={{
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean'],
          ],
        }}
        theme="snow"
        value={editorValue}
        onChange={onChangeEvent ? handleTextChange : setEditorValue}
        className={toolbarAtBottom ? 'rich-text-editor-toolbar-bottom' : undefined}
        style={style ? {backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : undefined, ...style} : {backgroundColor: theme.palette.mode === 'dark' ? '#000000' : undefined}}
        readOnly={disabled}
      />
      <style>
      {`
      .rich-text-editor-toolbar-bottom {
        display: flex;
        flex-direction: column;
      }
      .rich-text-editor-toolbar-bottom .ql-container {
        order: 1;
      }
      .rich-text-editor-toolbar-bottom .ql-toolbar {
        order: 2;
      }
      .rich-text-editor-toolbar-bottom .ql-toolbar.ql-snow,
      .rich-text-editor-toolbar-bottom .ql-container.ql-snow {
        border-left: 0 !important;
        border-right: 0 !important;
        border-bottom: 0 !important;
        border-color: transparent !important;
      }
      .rich-text-editor-toolbar-bottom .ql-toolbar.ql-snow {
        border-top: 1px solid #d0d0d0 !important;
      }
      /* Hide Quill's default list markers and use our custom ones */
      .ql-editor ol > li.custom-list-marker::before {
        content: attr(data-list-marker) !important;
      }
      `}
      {theme.palette.mode === 'dark' && `
      .ql-toolbar.ql-snow {
      background-color: ${theme.palette.background.default};
      border-color: ${theme.palette.gray.borderGray};
      }
      .ql-toolbar.ql-snow .ql-stroke {
      stroke: ${theme.palette.white.main};
      }
      .ql-toolbar.ql-snow .ql-fill {
      fill: ${theme.palette.white.main};
      }
      .ql-toolbar.ql-snow .ql-picker-label {
      color: ${theme.palette.white.main};
      }
      .ql-container.ql-snow {
      border-color: ${theme.palette.gray.borderGray};
      }
      .ql-editor.ql-blank::before {
      color: ${theme.palette.white.main};
      opacity: 0.6;
      }
      `}
      </style>
      </>
    );
  }
);
export default RichTextEditor;
